import { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import MaterialIcon from '../components/MaterialIcon';
import CommandAnalyticsStrip from '../components/analytics/CommandAnalyticsStrip';
import { AreaTrendChart, HorizontalMeters } from '../components/analytics/CommandCharts';
import api from '../api';
import {
  formatBytesToMb,
  formatDateTime,
  formatNumber,
  formatRelativeTime,
} from '../utils/formatters';

const CHART_WIDTH = 1000;
const CHART_HEIGHT = 200;
const CHART_PADDING = 12;

function buildSeriesPath(points = []) {
  if (!points.length) return { linePath: '', areaPath: '', pointsWithCoords: [] };
  const maxValue = Math.max(...points.map((point) => Number(point.value || 0)), 1);
  const usableWidth = CHART_WIDTH - CHART_PADDING * 2;
  const usableHeight = CHART_HEIGHT - CHART_PADDING * 2;
  const step = points.length > 1 ? usableWidth / (points.length - 1) : 0;

  const pointsWithCoords = points.map((point, index) => {
    const x = CHART_PADDING + index * step;
    const y =
      CHART_PADDING +
      usableHeight -
      (Number(point.value || 0) / maxValue) * usableHeight;
    return { ...point, x, y };
  });

  const linePath = pointsWithCoords
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x},${point.y}`)
    .join(' ');
  const first = pointsWithCoords[0];
  const last = pointsWithCoords[pointsWithCoords.length - 1];
  const areaPath = `${linePath} L ${last.x},${CHART_HEIGHT - CHART_PADDING} L ${first.x},${CHART_HEIGHT - CHART_PADDING} Z`;

  return { linePath, areaPath, pointsWithCoords };
}

function buildTimeBuckets(rows = [], bucketCount = 5, bucketHours = 3) {
  const now = new Date();
  const end = new Date(now);
  end.setMinutes(0, 0, 0);

  const buckets = Array.from({ length: bucketCount }, (_, index) => {
    const start = new Date(end.getTime() - (bucketCount - index - 1) * bucketHours * 3600000);
    return {
      start,
      end: new Date(start.getTime() + bucketHours * 3600000),
      label: start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }),
      value: 0,
    };
  });

  for (const row of rows) {
    const date = row?.createdAt ? new Date(row.createdAt) : null;
    if (!date || Number.isNaN(date.getTime())) continue;
    const match = buckets.find((bucket) => date >= bucket.start && date < bucket.end);
    if (match) match.value += 1;
  }

  return buckets;
}

function formatActionLabel(action = '') {
  return String(action || '').trim().replace(/\./g, '_').toUpperCase();
}

function getAuditStatus(action = '') {
  const normalized = String(action || '').toLowerCase();
  if (normalized.includes('revok') || normalized.includes('blocked') || normalized.includes('reject')) {
    return { label: 'BLOCKED', tone: 'text-[#fd6c00]' };
  }
  if (normalized.includes('rotate') || normalized.includes('updated') || normalized.includes('created')) {
    return { label: 'SUCCESS', tone: 'text-[#f1c100]' };
  }
  return { label: 'SECURE', tone: 'text-[#f1c100]' };
}

export default function PlatformHealth() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [health, setHealth] = useState(null);
  const [overview, setOverview] = useState(null);
  const [commandSummary, setCommandSummary] = useState(null);
  const [refreshingCommand, setRefreshingCommand] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [healthResponse, overviewResponse] = await Promise.all([
          api.get('/admin/platform/platform-health'),
          api.get('/admin/platform/overview'),
        ]);
        setHealth(healthResponse.data);
        setOverview(overviewResponse.data);
        setCommandSummary(healthResponse.data?.commandAnalytics ? {
          ...healthResponse.data.commandAnalytics,
          sources: healthResponse.data.sources || [],
          freshness: healthResponse.data.freshness || null,
          externalSummary: healthResponse.data.externalSummary || null,
        } : null);
      } catch (err) {
        setError(err?.response?.data?.message || 'Could not load platform health.');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  const refreshCommandAnalytics = async () => {
    setRefreshingCommand(true);
    try {
      const { data } = await api.post('/admin/platform/command-analytics/refresh', {
        range: '24h',
      });
      setCommandSummary(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not refresh command analytics.');
    } finally {
      setRefreshingCommand(false);
    }
  };

  const model = useMemo(() => {
    const backupStatus = health?.backupStatus || {};
    const backupTimeline = health?.backupTimeline || [];
    const systemsByStatus = health?.systemsByStatus || [];
    const recentActivity = overview?.recentActivity || [];
    const hardeningChecklist = health?.hardeningChecklist || [];
    const hardeningScore = Number(health?.hardeningScore || 0);
    const totalSystems = systemsByStatus.reduce((sum, item) => sum + Number(item.total || 0), 0);
    const healthySystems =
      systemsByStatus.find((item) => String(item._id || '').toLowerCase() === 'healthy')?.total || 0;
    const uptimeScore = totalSystems ? ((healthySystems / totalSystems) * 100).toFixed(2) : '0.00';
    const backupConfidence = backupStatus.latest?.ageHours != null
      ? Math.max(12, Math.min(100, Math.round(100 - backupStatus.latest.ageHours * 4)))
      : 0;
    const auditBuckets = buildTimeBuckets(recentActivity);
    const throughputValue = auditBuckets.reduce((sum, bucket) => sum + bucket.value, 0);
    const queueOpenCount =
      Number(health?.queueHealth?.openSupportCases || 0) +
      Number(health?.queueHealth?.openSocialReports || 0);
    const uptimeBars = Array.from({ length: 8 }, (_, index) => {
      const source = backupTimeline[index] || backupTimeline[backupTimeline.length - 1];
      if (!source) return 38;
      const sizeMb = Number(source.sizeBytes || 0) / (1024 * 1024);
      return Math.max(18, Math.min(100, Math.round(sizeMb)));
    });

    return {
      backupConfidence,
      hardeningScore,
      hardeningChecklist,
      postureHealthyCount: hardeningChecklist.filter((item) => item.healthy).length,
      uptimeScore,
      uptimeBars,
      throughputValue,
      queueOpenCount,
      throughputSeries: auditBuckets.map((bucket) => ({
        label: bucket.label,
        value: bucket.value || 1,
      })),
      releaseVersion: health?.releaseStatus?.currentVersion || overview?.releaseStatus?.generatedAt || 'Unavailable',
      releaseBuild: health?.releaseStatus?.buildNumber || 'Unknown',
      latestBackupLabel: backupStatus.latest?.sizeBytes ? formatBytesToMb(backupStatus.latest.sizeBytes) : 'No backup',
      latestBackupAge: backupStatus.latest?.modifiedAt
        ? formatRelativeTime(backupStatus.latest.modifiedAt)
        : 'Not recorded',
      releaseProgress: Math.max(18, Math.min(96, Math.round(Number(uptimeScore)))),
      auditTrail: recentActivity.slice(0, 4),
      snapshotGeneratedAt: health?.snapshotGeneratedAt,
    };
  }, [health, overview]);

  const chartPaths = useMemo(
    () => buildSeriesPath(model.throughputSeries.length ? model.throughputSeries : [{ label: '00:00', value: 1 }]),
    [model.throughputSeries]
  );

  return (
    <Layout searchPlaceholder="Search parameters...">
      <div className="space-y-8">
        {error ? (
          <div className="rounded-lg border border-[#93000a] bg-[#2a1112] px-4 py-3 text-sm text-[#ffdad6]">
            {error}
          </div>
        ) : null}

        {commandSummary ? (
          <CommandAnalyticsStrip
            summary={commandSummary}
            title="Operations telemetry"
            description="Freshness, fallback, and command analytics from Shimbirr, PMS, Taban Food, and the main Taban Website."
            onRefresh={refreshCommandAnalytics}
            refreshing={refreshingCommand}
            compact
          />
        ) : null}

        <section className="space-y-8">
          <div className="space-y-2">
            <span className="font-['Geist'] text-xs font-bold uppercase tracking-[0.22em] text-[#f1c100]">
              System Oversight
            </span>
            <h1 className="font-['Geist'] text-[clamp(2rem,5vw,3rem)] font-black tracking-[-0.03em] text-white">
              Platform Health
            </h1>
            <p className="max-w-2xl text-lg text-[#d2c5ab]">
              High-fidelity monitoring of hardening protocols, industrial release stability, and active operational posture.
            </p>
          </div>

          {loading ? (
            <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6 text-sm text-[#d2c5ab]">
              Loading platform telemetry...
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                  <div className="flex items-start justify-between">
                    <span className="text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">
                      Backup Confidence
                    </span>
                    <MaterialIcon name="shield" className="text-[#f1c100]" />
                  </div>
                  <div className="mt-4 flex items-end gap-2">
                    <span className="font-['Geist'] text-5xl font-black text-[#f1c100]">
                      {model.backupConfidence}%
                    </span>
                    <span className="mb-1 text-sm font-bold text-[#ffedc3]">
                      {model.latestBackupAge}
                    </span>
                  </div>
                  <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[#353534]">
                    <div
                      className="h-full bg-[#f1c100]"
                      style={{ width: `${model.backupConfidence}%` }}
                    />
                  </div>
                  <p className="mt-4 text-sm text-[#d2c5ab]">
                    Latest snapshot {model.latestBackupLabel} across {formatNumber(health?.backupStatus?.totalSnapshots || 0)} stored recovery points.
                  </p>
                </div>

                <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                  <div className="flex items-start justify-between">
                    <span className="text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">
                      Posture Status
                    </span>
                    <MaterialIcon name="security" className="text-[#f1c100]" />
                  </div>
                  <div className="mt-5 flex items-center gap-3">
                    <div className="status-pulse h-3 w-3 rounded-full bg-[#f1c100]" />
                    <span className="font-['Geist'] text-[2rem] font-bold text-white">
                      {model.hardeningScore >= 80 ? 'HARDENED' : 'VALIDATING'}
                    </span>
                  </div>
                  <p className="mt-2 text-base text-[#d2c5ab]">
                    Threat Level: {model.hardeningScore >= 80 ? 'MINIMAL' : 'MODERATE'}
                  </p>
                  <div className="mt-6 flex gap-2">
                    <span className="rounded bg-[#353534] px-2 py-1 text-[10px] font-bold text-[#f1c100]">
                      DMZ: ACTIVE
                    </span>
                    <span className="rounded bg-[#353534] px-2 py-1 text-[10px] font-bold text-[#f1c100]">
                      IAM: {model.postureHealthyCount}/{model.hardeningChecklist.length}
                    </span>
                  </div>
                </div>

                <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                  <div className="flex items-start justify-between">
                    <span className="text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">
                      Uptime Reliability
                    </span>
                    <MaterialIcon name="timer" className="text-[#f1c100]" />
                  </div>
                  <div className="mt-4 flex items-end gap-2">
                    <span className="font-['Geist'] text-5xl font-black text-white">
                      {model.uptimeScore}%
                    </span>
                    <span className="mb-1 text-sm font-bold text-[#d2c5ab]">
                      live
                    </span>
                  </div>
                  <div className="mt-4 flex h-8 gap-1">
                    {model.uptimeBars.map((value, index) => (
                      <div
                        key={`${value}-${index}`}
                        className="flex-1 self-end bg-[#f1c100]"
                        style={{
                          height: `${value}%`,
                          opacity: index === 3 ? 1 : 0.4,
                        }}
                      />
                    ))}
                  </div>
                  <p className="mt-4 text-sm text-[#d2c5ab]">
                    Snapshot generated {formatRelativeTime(model.snapshotGeneratedAt)} with {formatNumber(model.queueOpenCount)} open operations signals.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">
                        Queue Pressure
                      </span>
                      <h3 className="mt-1 font-['Geist'] text-2xl font-semibold text-white">
                        Compute Throughput
                      </h3>
                    </div>
                    <div className="glow-orange rounded-full bg-[#fd6c00] px-3 py-1 text-xs font-bold text-[#562000]">
                      {model.queueOpenCount > 0 ? 'SPIKE DETECTED' : 'STABLE'}
                    </div>
                  </div>

                  <div className="relative mt-6 h-64 w-full border-b border-l border-[#4e4632]">
                    <svg className="h-full w-full" preserveAspectRatio="none" viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}>
                      <path d={chartPaths.areaPath} fill="rgba(241, 193, 0, 0.1)" />
                      <path d={chartPaths.linePath} fill="none" stroke="#f1c100" strokeWidth="4" />
                      <line
                        x1="0"
                        x2={CHART_WIDTH}
                        y1="54"
                        y2="54"
                        stroke="#fd6c00"
                        strokeDasharray="12 10"
                        strokeWidth="2"
                      />
                    </svg>
                    <div className="absolute right-0 top-4 text-[10px] font-bold text-[#fd6c00]">
                      CRITICAL THRESHOLD
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-5 text-sm text-[#d2c5ab]">
                    {model.throughputSeries.map((point) => (
                      <span key={point.label}>{point.label}</span>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                  <div>
                    <span className="text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">
                      Release Status
                    </span>
                    <h3 className="mt-1 font-['Geist'] text-2xl font-semibold text-white">
                      Active Deployment Pipeline
                    </h3>
                  </div>

                  <div className="relative mt-6 space-y-6">
                    <div className="absolute bottom-2 left-[15px] top-2 w-0.5 bg-[#353534]" />
                    <ReleaseStep
                      icon="check"
                      iconTone="bg-[#f1c100] text-[#241a00]"
                      title="STAGING_A (HARDENING)"
                      meta="COMPLETED"
                      progress={100}
                      progressTone="#f1c100"
                    />
                    <ReleaseStep
                      icon="sync"
                      iconTone="bg-[#fd6c00] text-white"
                      title={`PRODUCTION_C (${model.releaseBuild})`}
                      meta={`IN PROGRESS (${model.releaseProgress}%)`}
                      progress={model.releaseProgress}
                      progressTone="#fd6c00"
                      body={`Deploying ${model.releaseVersion} with ${formatNumber(model.queueOpenCount)} active queue signals.`}
                    />
                    <ReleaseStep
                      icon="hourglass_empty"
                      iconTone="bg-[#353534] text-[#d2c5ab]"
                      title="GLOBAL_SYNC (VERIFICATION)"
                      meta={model.queueOpenCount > 0 ? 'QUEUED' : 'READY'}
                      progress={model.queueOpenCount > 0 ? 0 : 100}
                      progressTone="#f1c100"
                      muted
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h2 className="font-['Geist'] text-2xl font-semibold text-white">
                  Security Audit Trail
                </h2>
                <div className="overflow-x-auto rounded-lg border border-[#4e4632]">
                  <table className="w-full border-collapse bg-[#1c1b1b] text-left">
                    <thead>
                      <tr className="border-b border-[#353534]">
                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#d2c5ab]">
                          Timestamp
                        </th>
                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#d2c5ab]">
                          Event Type
                        </th>
                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#d2c5ab]">
                          Target Asset
                        </th>
                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#d2c5ab]">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#353534]">
                      {model.auditTrail.length ? (
                        model.auditTrail.map((row) => {
                          const status = getAuditStatus(row.action);
                          return (
                            <tr key={row.id}>
                              <td className="px-6 py-4 font-mono text-sm text-white">
                                {formatDateTime(row.createdAt)}
                              </td>
                              <td className="px-6 py-4 text-white">
                                {formatActionLabel(row.action)}
                              </td>
                              <td className="px-6 py-4 text-[#d2c5ab]">
                                {row.targetLabel || row.actorLabel || row.targetType || 'SYSTEM_NODE'}
                              </td>
                              <td className={`px-6 py-4 font-bold ${status.tone}`}>
                                {status.label}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-6 py-8 text-center text-sm text-[#d2c5ab]">
                            No audit activity has been recorded yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {commandSummary?.aggregateSeries?.[0]?.points?.length ? (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.25fr,0.95fr]">
                  <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">
                          Cross-product throughput
                        </span>
                        <h3 className="mt-1 font-['Geist'] text-2xl font-semibold text-white">
                          Unified command activity
                        </h3>
                      </div>
                    </div>
                    <div className="mt-6">
                      <AreaTrendChart
                        points={commandSummary.aggregateSeries[0].points}
                        color="#FFCC00"
                        accent="#FF6D00"
                      />
                    </div>
                  </div>

                  <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                    <div>
                      <span className="text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">
                        Queue pressure
                      </span>
                      <h3 className="mt-1 font-['Geist'] text-2xl font-semibold text-white">
                        Cross-product operations load
                      </h3>
                    </div>
                    <div className="mt-6">
                      <HorizontalMeters
                        items={(commandSummary.aggregateQueues || []).slice(0, 5).map((item) => ({
                          label: `${item.productLabel} · ${item.label}`,
                          value: item.value,
                          meta: item.tone || 'queue',
                          color: item.tone === 'critical' ? '#FD6C00' : '#FFCC00',
                          accent: item.tone === 'critical' ? '#FFB692' : '#FFE08B',
                        }))}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>
    </Layout>
  );
}

function ReleaseStep({
  icon,
  iconTone,
  title,
  meta,
  progress,
  progressTone,
  body,
  muted = false,
}) {
  return (
    <div className={`relative flex gap-4 ${muted ? 'opacity-60' : ''}`}>
      <div className={`z-10 flex h-8 w-8 items-center justify-center rounded-full border-4 border-[#131313] ${iconTone}`}>
        <MaterialIcon name={icon} className="text-sm" />
      </div>
      <div className="flex-1">
        <div className="flex justify-between gap-4">
          <span className="font-bold text-white">{title}</span>
          <span className={`text-sm font-semibold ${muted ? 'text-[#d2c5ab]' : progressTone === '#fd6c00' ? 'text-[#fd6c00]' : 'text-[#d2c5ab]'}`}>
            {meta}
          </span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#353534]">
          <div
            className="h-full"
            style={{ width: `${progress}%`, backgroundColor: progressTone }}
          />
        </div>
        {body ? <p className="mt-2 text-sm text-[#d2c5ab]">{body}</p> : null}
      </div>
    </div>
  );
}
